import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Alert, InputGroup, Breadcrumb } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CREATE_CHECKLIST } from 'actions/formActions';
import { cookie } from 'utils/cookies';

import {
  IconPlug,
  IconBolt,
  IconBox,
  IconSitemap,
  IconChargingPile,
  IconReplace,
  IconLayoutGrid,
  IconArrowLeft,
  IconPlus,
  IconMinus,
  IconSend,
  IconHash,
  IconShieldCheck
} from '@tabler/icons-react';

const checklistItems = [
    'APFC Panel',
    'Cabel Tray',
    'DB Panel',
    'Earthing',
    'HT Cabel',
    'Lightning Arrester',
    'LT Cabel',
    'LT Panel',
    'Transformer',
    'VCB',
    'HOTO (Project Handover Takeover Report)',
    'Canopy',
];

const checklistIcons = {
    'APFC Panel': <IconPlug size={20} />,
    'Cabel Tray': <IconBox size={20} />,
    'DB Panel': <IconLayoutGrid size={20} />,
    'Earthing': <IconSitemap size={20} />,
    'HT Cabel': <IconBolt size={20} />,
    'Lightning Arrester': <IconBolt size={20} />,
    'LT Cabel': <IconBolt size={20} />,
    'LT Panel': <IconBolt size={20} />,
    'Transformer': <IconReplace size={20} />,
    'VCB': <IconChargingPile size={20} />,
    'HOTO (Project Handover Takeover Report)': <IconShieldCheck size={20} />,
    'Canopy': <IconBox size={20} />,
};

const FormsChecklist = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userRole = cookie.get('user-role');

    const [stationId, setStationId] = useState('');
    const [stationName, setStationName] = useState('');
    const [quantities, setQuantities] = useState(
        checklistItems.reduce((acc, item) => ({ ...acc, [item]: 0 }), {})
    );
    const [errors, setErrors] = useState({ stationId: '', checklist: '', stationName: '' });

    if (userRole !== 'engineer' && userRole !== 'admin') {
        return (
            <div className="container-xl mt-5">
                <div className="glass-panel p-5 text-center">
                    <div className="mb-4 text-danger"><IconBox size={64} /></div>
                    <h2 className="fw-bold">Access Denied</h2>
                    <p className="text-muted">Only engineers and admins are authorized to create new station checklists.</p>
                    <Button variant="outline-primary" className="mt-3 px-5 py-2" onClick={() => navigate('/formList')}>
                        Return to Overview
                    </Button>
                </div>
            </div>
        );
    }

    const handleQuantityChange = (item, value) => {
        const qty = parseInt(value, 10) || 0;
        setQuantities((prev) => ({ ...prev, [item]: qty }));
        if (errors.checklist) setErrors((prev) => ({ ...prev, checklist: '' }));
    };

    const adjustQuantity = (item, delta) => {
        setQuantities((prev) => {
            const updated = Math.max(0, (prev[item] || 0) + delta);
            return { ...prev, [item]: updated };
        });
    };

    const validate = () => {
        const validStationId = /^[A-Z0-9]+$/.test(stationId);
        const validStationName = stationName.trim() !== '';
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + (qty > 0 ? 1 : 0), 0);

        const newErrors = {
            stationId: validStationId ? '' : 'Station ID must contain only letters and numbers.',
            stationName: validStationName ? '' : 'Station Name / Project Name is required.',
            checklist: totalSelected > 0 ? '' : 'Select at least one item with quantity greater than 0.',
        };

        setErrors(newErrors);
        return validStationId && validStationName && totalSelected > 0;
    };

    const handleSubmit = useCallback(() => {
        if (!validate()) return;

        const checklist = {};
        Object.entries(quantities).forEach(([item, quantity]) => {
            if (quantity > 0) {
                const key = item
                    .toLowerCase()
                    .replace(/\(.*?\)/g, '')
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '');
                checklist[key] = quantity;
            }
        });

        const payload = {
            stationId: stationId.toUpperCase(),
            project_name: stationName.toUpperCase(),
            checklist,
        };

        dispatch({
            type: CREATE_CHECKLIST,
            payload,
            cb: (res) => {
                if (res.status === 200) {
                    navigate(`/formList/${payload.stationId}`);
                }
            },
        });
    }, [validate, quantities, stationId, stationName, dispatch]);

    const totalFormsSelected = Object.values(quantities).reduce((a, b) => a + b, 0);

    return (
        <div className="page-body">
            <div className="container-xl" style={{ maxWidth: '720px' }}>
              {/* Breadcrumbs */}
              <div className="mb-3">
                <Breadcrumb>
                  <Breadcrumb.Item href="#" onClick={() => navigate('/formList')}>All Sites</Breadcrumb.Item>
                  <Breadcrumb.Item active>New Site Registration</Breadcrumb.Item>
                </Breadcrumb>
              </div>

              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="page-title m-0" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                    Create Station Checklist
                  </h2>
                  <p className="text-muted small mt-1 mb-0">Define required forms for a new project site</p>
                </div>
                <Button variant="outline-secondary" className="px-3 py-2" style={{ borderRadius: '10px' }} onClick={() => navigate('/formList')}>
                  <IconArrowLeft size={16} className="me-2" /> Back
                </Button>
              </div>

              {/* Main Panel */}
              <div style={{
                background: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(12px)',
                border: '1.5px solid #e2e8f0',
                borderRadius: '20px',
                padding: '32px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              }}>
                <Form>
                  {/* Station ID & Project Name */}
                  <Form.Group className="mb-3" controlId="stationId" style={{ maxWidth: '280px' }}>
                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>Station ID</Form.Label>
                    <InputGroup style={{ borderRadius: '12px', overflow: 'hidden', border: errors.stationId ? '1.5px solid #dc3545' : '1.5px solid #e2e8f0' }}>
                      <InputGroup.Text style={{ background: '#f8fafc', border: 'none' }}><IconHash size={16} color="#94a3b8" /></InputGroup.Text>
                      <Form.Control
                        style={{ border: 'none', boxShadow: 'none', fontWeight: 600 }}
                        type="text"
                        placeholder="e.g. STN001"
                        value={stationId}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                            setStationId(value);
                            if (errors.stationId) setErrors((prev) => ({ ...prev, stationId: '' }));
                        }}
                      />
                    </InputGroup>
                    {errors.stationId && <div className="text-danger small mt-1">{errors.stationId}</div>}
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="stationName">
                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>Project Name</Form.Label>
                    <InputGroup style={{ borderRadius: '12px', overflow: 'hidden', border: errors.stationName ? '1.5px solid #dc3545' : '1.5px solid #e2e8f0' }}>
                      <InputGroup.Text style={{ background: '#f8fafc', border: 'none' }}><IconBox size={16} color="#94a3b8" /></InputGroup.Text>
                      <Form.Control
                        style={{ border: 'none', boxShadow: 'none', fontWeight: 600 }}
                        type="text"
                        placeholder="e.g. ALPHA RENEWABLES"
                        value={stationName}
                        onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setStationName(value);
                            if (errors.stationName) setErrors((prev) => ({ ...prev, stationName: '' }));
                        }}
                      />
                    </InputGroup>
                    {errors.stationName && <div className="text-danger small mt-1">{errors.stationName}</div>}
                  </Form.Group>

                  {/* Divider */}
                  <hr style={{ border: 'none', borderTop: '1.5px solid #e2e8f0', margin: '8px 0 20px' }} />

                  {/* Form Types Header */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="m-0 fw-bold" style={{ color: '#334155' }}>Form Types</h6>
                    <span style={{
                      background: totalFormsSelected > 0 ? '#ecfdf5' : '#f1f5f9',
                      color: totalFormsSelected > 0 ? '#059669' : '#94a3b8',
                      padding: '4px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}>
                      {totalFormsSelected} forms selected
                    </span>
                  </div>

                  {/* Form Types List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {checklistItems.map((item) => {
                      const isActive = quantities[item] > 0;
                      return (
                        <div
                          key={item}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            borderRadius: '14px',
                            border: isActive ? '1.5px solid #4facfe' : '1.5px solid #e2e8f0',
                            background: isActive ? 'rgba(79, 172, 254, 0.04)' : '#fff',
                          }}
                        >
                          {/* Left: Icon + Name */}
                          <div className="d-flex align-items-center gap-3">
                            <div style={{
                              width: '38px', height: '38px', borderRadius: '10px',
                              background: isActive ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'linear-gradient(135deg, #f0f4ff, #e8f0fe)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: isActive ? '#fff' : '#4facfe',
                              flexShrink: 0,
                            }}>
                              {checklistIcons[item] || <IconBox size={20} />}
                            </div>
                            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
                              {item.split('(')[0].trim()}
                            </span>
                          </div>

                          {/* Right: Quantity Controls */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '10px',
                            border: '1.5px solid #e2e8f0',
                            overflow: 'hidden',
                            background: '#fff',
                            flexShrink: 0,
                          }}>
                            <button
                              type="button"
                              onClick={() => adjustQuantity(item, -1)}
                              disabled={quantities[item] === 0}
                              style={{
                                width: '34px', height: '34px', border: 'none',
                                background: '#f8fafc', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                cursor: quantities[item] === 0 ? 'not-allowed' : 'pointer',
                                opacity: quantities[item] === 0 ? 0.4 : 1,
                              }}
                            >
                              <IconMinus size={14} color="#64748b" />
                            </button>
                            <input
                              type="number"
                              value={quantities[item]}
                              min={0}
                              onChange={(e) => handleQuantityChange(item, e.target.value)}
                              style={{
                                width: '44px', textAlign: 'center', border: 'none',
                                fontSize: '15px', fontWeight: 800, outline: 'none',
                                color: isActive ? '#4facfe' : '#94a3b8',
                                background: 'transparent',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => adjustQuantity(item, 1)}
                              style={{
                                width: '34px', height: '34px', border: 'none',
                                background: '#f8fafc', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <IconPlus size={14} color="#64748b" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Errors & Submit */}
                  {errors.checklist && (
                    <Alert variant="danger" className="border-0 mt-3 mb-0" style={{ borderRadius: '12px', fontSize: '13px' }}>
                      {errors.checklist}
                    </Alert>
                  )}

                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="w-100 border-0 d-flex align-items-center justify-content-center gap-2 mt-4"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 800,
                      borderRadius: '14px',
                      padding: '14px',
                      fontSize: '15px',
                      letterSpacing: '0.02em',
                    }}
                    onClick={handleSubmit}
                  >
                    <IconSend size={18} /> Create Station
                  </Button>
                </Form>
              </div>
            </div>
        </div>
    );
};

export default FormsChecklist;
